import React, { createContext, useContext, useState, useEffect } from 'react';
import API_BASE_URL from './api';

const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(localStorage.getItem('userToken'));
  const [loading, setLoading] = useState(true);

  // On mount, if token exists, fetch profile
  useEffect(() => {
    const initAuth = async () => {
      const savedToken = localStorage.getItem('userToken');
      if (savedToken) {
        try {
          const res = await fetch(`${API_BASE_URL}/users/profile`, {
            headers: { Authorization: `Bearer ${savedToken}` }
          });
          const data = await res.json();
          if (data.success) {
            setUser(data.user);
            setToken(savedToken);
          } else {
            // Token invalid, clear
            localStorage.removeItem('userToken');
            localStorage.removeItem('userData');
            setToken(null);
            setUser(null);
          }
        } catch {
          localStorage.removeItem('userToken');
          localStorage.removeItem('userData');
          setToken(null);
          setUser(null);
        }
      }
      setLoading(false);
    };
    initAuth();
  }, []);

  const loginUser = async (email, password) => {
    const res = await fetch(`${API_BASE_URL}/users/login`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email, password })
    });
    const data = await res.json();
    if (data.success) {
      localStorage.setItem('userToken', data.token);
      localStorage.setItem('userData', JSON.stringify(data.user));
      setToken(data.token);
      setUser(data.user);
    }
    return data;
  };

  const registerUser = async (formData) => {
    const res = await fetch(`${API_BASE_URL}/users/register`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(formData)
    });
    const data = await res.json();
    if (data.success && data.token) {
      localStorage.setItem('userToken', data.token);
      localStorage.setItem('userData', JSON.stringify(data.user));
      setToken(data.token);
      setUser(data.user);
    }
    return data;
  };

  const loginWithGoogle = () => {
    window.location.href = `${API_BASE_URL}/users/auth/google`;
  };

  const setAuthData = (newToken, newUser) => {
    localStorage.setItem('userToken', newToken);
    localStorage.setItem('userData', JSON.stringify(newUser));
    setToken(newToken);
    setUser(newUser);
  };

  const logout = () => {
    localStorage.removeItem('userToken');
    localStorage.removeItem('userData');
    setToken(null);
    setUser(null);
  };

  const value = {
    user,
    token,
    isAuthenticated: !!token && !!user,
    loading,
    login: loginUser,
    register: registerUser,
    loginWithGoogle,
    setAuthData,
    logout
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export default AuthContext;
